import { spacing } from "globalStyles";
import { FooterSection } from "interfaces/content";
import { useAppContext } from "providers/App";
import { useMemo } from "react";
import styled, { css } from "styled-components";
import { breakpoints } from "themes/breakpoints";
import InlineDetails from "ui/InlineDetails";
import { parseToLinkType } from "utils/parse";
import CopyrightSection from "./CopyrightSection";

interface FooterProps {
  data: FooterSection["data"];
}

const Footer = ({ data }: FooterProps) => {
  const { content } = useAppContext();

  const leftColumnInfo = useMemo(() => {
    const result = [];

    if (content?.contacts.entity?.address) {
      result.push({
        title: "",
        details: [
          {
            label: "Morada",
            value: content?.contacts.entity.address,
          },
        ],
      });
    }

    if (content?.contacts.entity?.phone) {
      result.push({
        title: "",
        details: [
          {
            label: "Contacto",
            value:
              data.info.hasMultiplePhones && content?.contacts.datarede?.phone
                ? [
                    parseToLinkType(content.contacts.entity.phone, "phone"),
                    parseToLinkType(content.contacts.datarede?.phone, "phone"),
                  ]
                : parseToLinkType(content.contacts.entity.phone, "phone"),
            supportText:
              content.contacts.entity.phoneSupportText ||
              (data.info.hasMultiplePhones && content?.contacts.datarede?.phone
                ? content.contacts.datarede?.phoneSupportText
                : ""),
          },
        ],
      });
    }

    return result;
  }, [content, data.info.hasMultiplePhones]);

  const rightColumnInfo = useMemo(() => {
    const result = [];

    if (data.info.schedule.opening?.length > 0) {
      result.push({
        title: "Horário de funcionamento",
        details: data.info.schedule.opening.map((schedule) => ({
          label: schedule.days,
          value: schedule.hours,
        })),
      });
    }

    if (data.info.schedule.attendance?.length > 0) {
      result.push({
        title: "Horário de atendimento",
        details: data.info.schedule.attendance.map((schedule) => ({
          label: schedule.days,
          value: schedule.hours,
        })),
      });
    }

    return result;
  }, [data.info.schedule.attendance, data.info.schedule.opening]);

  const hasTopInfo = leftColumnInfo.length > 0 || rightColumnInfo.length > 0;

  return (
    <Container id={data.id} className="anchor-offset">
      <Wrapper className="content-container">
        <AuxContainer hasTopInfo={hasTopInfo}>
          <InfoContainer>
            <InfoContent>
              {leftColumnInfo.map((info, index) => (
                <InlineDetails
                  key={`${info.title}-${index}`}
                  title={info.title}
                  details={info.details}
                  verticalGap={spacing.small}
                />
              ))}
            </InfoContent>
            <InfoContent>
              {rightColumnInfo.map((info, index) => (
                <InlineDetails
                  key={`${info.title}-${index}`}
                  details={info.details}
                  title={info.title}
                />
              ))}
            </InfoContent>
          </InfoContainer>
          <CopyrightContainer hasTopInfo={hasTopInfo}>
            <CopyrightSection className="copyright-section" />
          </CopyrightContainer>
        </AuxContainer>
      </Wrapper>
    </Container>
  );
};

export default Footer;

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.background.primary};
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Wrapper = styled.div`
  flex: 1;
  max-width: 1000px;
  margin: 0 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.secondary};
  margin-bottom: ${spacing.extraLarge}px;

  @media (max-width: ${breakpoints.md}) {
    display: flex;
    justify-content: center;
    margin-bottom: 120px;
  }
`;

const AuxContainer = styled.div<{ hasTopInfo: boolean }>`
  padding: ${({ hasTopInfo }) => (hasTopInfo ? spacing.large : 0)}px 0;
  display: flex;
  flex-direction: column;

  @media (max-width: ${breakpoints.md}) {
    margin: 0 auto;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    max-width: max-content;
    gap: ${spacing.extraLarge}px;
  }
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.extraLarge}px;
`;

const CopyrightContainer = styled.div<{ hasTopInfo: boolean }>`
  display: flex;
  justify-content: center;

  @media (max-width: ${breakpoints.md}) {
    justify-content: flex-start;
  }

  .copyright-section {
    ${({ hasTopInfo }) =>
      hasTopInfo &&
      css`
        margin-top: 140px;
      `}

    @media (max-width: ${breakpoints.md}) {
      margin-top: ${spacing.extraLarge}px;
    }
  }
`;
